// Talking to the grid: discovery over HTTP, then login over a session.

import { CreateAccountRequest, LoginRequest, type CreateAccountResponse, type Endpoint, type LoginResponse } from "./messages";
import { Session } from "./transport";

export interface GridInfo {
  product_name: string;
  grid_name: string;
  grid_nick: string;
  currency_symbol: string;
  engine_version: string;
  protocol_version: number;
  login_url: string;
  websocket_url: string;
  certificate_hash: string;
  registration_open: boolean;
  /** Whether a new account needs an invite code. */
  invite_only?: boolean;
  /** Whether the grid keeps a device logged in when asked; missing on a grid from before it could. */
  keeps_logins?: boolean;
  regions_online: number;
  agents_online: number;
  motd: string;
  notice: string;
  /** What the grid suggests a viewer see, for anyone who has not set
   *  their own. A region may suggest something different; zero at either
   *  level means no opinion. */
  draw_distance?: number;
  max_avatars?: number;
  /** The terms of service everybody agrees to, by version; empty when the
   *  grid asks nobody to. */
  terms_version?: string;
  /** How much of the currency a US dollar buys; nought, or missing, where
   *  it is not for sale. With the smallest and largest purchase, in whole
   *  dollars. */
  currency_per_usd?: number;
  currency_min_usd?: number;
  currency_max_usd?: number;
  /** Said to everybody buying. */
  currency_note?: string;
  /** The Library folder of the ready-made avatar a new avatar arrives in,
   *  as staff chose it; nil, or missing, for the grid's plain clothes. */
  starter_look?: string;
  /** Whether the grid can carry voice calls; missing from a grid older
   *  than the field: see `gridCarriesCalls`. */
  voice_calls?: boolean;
}

/**
 * Whether the grid carries voice calls, as its info says. A grid older
 * than the field says nothing, and is taken to carry them, as the viewer
 * took every grid before: the grid and the viewer are put out separately,
 * and Call taken away for want of a field would be taken from everybody.
 */
export function gridCarriesCalls(info: Pick<GridInfo, "voice_calls">): boolean {
  return info.voice_calls ?? true;
}

export const CLIENT_VERSION = "starfall-web/0.1";

/** Normalise whatever the user typed into the grid's HTTP base URL. */
export function normalizeGridUrl(input: string): string {
  let url = input.trim();
  if (!url) return "";
  if (!/^https?:\/\//i.test(url)) url = `http://${url}`;
  return url.replace(/\/+$/, "");
}

export async function fetchGridInfo(gridUrl: string): Promise<GridInfo> {
  const resp = await fetch(`${gridUrl}/api/grid/info`, { headers: { accept: "application/json" } });
  if (!resp.ok) throw new Error(`grid answered ${resp.status}`);
  return (await resp.json()) as GridInfo;
}

export function loginEndpoint(info: GridInfo): Endpoint {
  const url = new URL(info.login_url);
  return {
    host: url.hostname,
    port: Number(url.port || 443),
    certificate_hash: info.certificate_hash,
    websocket_url: info.websocket_url,
  };
}

function machineId(): Uint8Array {
  const key = "starfall.machine";
  let id = "";
  try {
    id = localStorage.getItem(key) ?? "";
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
  } catch {
    id = "browser";
  }
  return new TextEncoder().encode(id);
}

/**
 * How this device is kept logged in: `remember` asks the grid for a login
 * token with the password, and `token` is the one it gave last time, sent
 * instead of the password.
 */
export interface Keeping {
  remember?: boolean;
  token?: string;
}

export async function login(
  info: GridInfo,
  username: string,
  password: string,
  startLocation: string,
  acceptTerms = "",
  keeping: Keeping = {},
): Promise<LoginResponse> {
  const session = await Session.connect(loginEndpoint(info), "/login");
  try {
    session.send({
      type: "LoginRequest",
      body: {
        ...LoginRequest.create(),
        username,
        password,
        client_version: CLIENT_VERSION,
        platform: navigator.platform || "web",
        start_location: startLocation,
        machine_id: machineId(),
        accept_terms: acceptTerms,
        remember: keeping.remember === true,
        login_token: keeping.token ?? "",
      },
    });
    return await session.waitFor((m) => (m.type === "LoginResponse" ? m.body : undefined), 20000);
  } finally {
    session.close(0, "login done");
  }
}

/** A new account, as the login screen's form has it. */
export interface NewAccount {
  /** The login: an older grid's username, or for a name from the grid's list its handle, 'nova.starwick'. */
  username: string;
  password: string;
  displayName: string;
  email: string;
  inviteCode: string;
  acceptTerms: string;
  /** The first name typed and the last name chosen, on a grid with a list of last names. */
  firstName?: string;
  lastName?: string;
  /** YYYY-MM-DD, and the ISO code of the country lived in, asked with a name from the list. */
  birthDate?: string;
  country?: string;
}

/** What the viewer sends to make an account. */
export function createAccountRequest(account: NewAccount): CreateAccountRequest {
  // A name from the grid's list goes with the birth date and country asked with it.
  const named = account.firstName && account.lastName ? account : undefined;
  return {
    ...CreateAccountRequest.create(),
    username: account.username,
    password: account.password,
    display_name: account.displayName,
    email: account.email,
    invite_code: account.inviteCode,
    accept_terms: account.acceptTerms,
    first_name: named?.firstName ?? "",
    last_name: named?.lastName ?? "",
    birth_date: named?.birthDate ?? "",
    country: named?.country ?? "",
  };
}

export async function createAccount(info: GridInfo, account: NewAccount): Promise<CreateAccountResponse> {
  const body = createAccountRequest(account);
  const session = await Session.connect(loginEndpoint(info), "/login");
  try {
    session.send({ type: "CreateAccountRequest", body });
    return await session.waitFor((m) => (m.type === "CreateAccountResponse" ? m.body : undefined), 20000);
  } finally {
    session.close(0, "done");
  }
}
